import { useState, useEffect, useRef } from 'react';
import {
  Activity,
  Globe,
  Search,
  Gauge,
  Play,
  Square,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wifi,
  Network,
} from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';

interface DiagnosticToolsProps {
  edgeId?: string;
  edgeOnline?: boolean;
}

interface DiagnosticResult {
  requestId: string;
  type: string;
  status: 'running' | 'completed' | 'failed' | 'stopped';
  startTime: Date;
  endTime?: Date;
  results: string[];
  error?: string;
}

interface Capability {
  id: string;
  name: string;
  description: string;
  available: boolean;
  supportsStop: boolean;
}

interface LogicalInterface {
  id: string;
  name: string;
  ipv4: string | null;
  subnet: string | null;
  dhcp_client: boolean;
  operationalState: string;
}

const DiagnosticTools = ({ edgeId, edgeOnline: edgeOnlineProp = true }: DiagnosticToolsProps) => {
  const { canUpdate, canDelete } = usePermissions();
  const [activeTab, setActiveTab] = useState('ping');
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [interfaces, setInterfaces] = useState<LogicalInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTest, setCurrentTest] = useState<DiagnosticResult | null>(null);
  const edgeOnline = edgeOnlineProp;
  const eventSourceRef = useRef<EventSource | null>(null);

  // Ping form state
  const [pingForm, setPingForm] = useState({
    destination: '',
    count: 5,
    interval: 1,
    packetSize: 64,
    sourceInterface: '',
  });

  // Traceroute form state
  const [tracerouteForm, setTracerouteForm] = useState({
    destination: '',
    maxHops: 30,
    timeout: 3,
    protocol: 'icmp',
    sourceInterface: '',
  });

  // DNS form state
  const [dnsForm, setDnsForm] = useState({
    hostname: '',
    dnsServer: '',
    recordType: 'A',
    sourceInterface: '',
  });

  // Port scan form state
  const [portScanForm, setPortScanForm] = useState({
    target: '',
    ports: '80,443',
    timeout: 5,
    sourceInterface: '',
  });

  // Speed test form state
  const [speedTestForm, setSpeedTestForm] = useState({
    server: '',
    duration: 10,
    direction: 'both',
    sourceInterface: '',
  });

  // Fetch capabilities and interfaces on mount
  useEffect(() => {
    if (!edgeId) return;

    const fetchCapabilities = async () => {
      try {
        const response = await api.get(`/api/v2/edges/${edgeId}/diagnostics/capabilities`);
        setCapabilities(response.data?.capabilities || []);
      } catch (err) {
        console.error('Failed to fetch capabilities:', err);
      }
    };

    const fetchInterfaces = async () => {
      try {
        const response = await api.get(`/api/v2/edges/${edgeId}/interfaces/logical`);
        const allInterfaces = response.data.items || [];
        // Show all configured interfaces (don't filter by DHCP or operational state)
        setInterfaces(allInterfaces);
      } catch (err) {
        console.error('Failed to fetch interfaces:', err);
      }
    };

    fetchCapabilities();
    fetchInterfaces();
  }, [edgeId]);

  // Cleanup SSE on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const connectSSE = (requestId: string, tool: string) => {
    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const token = useAuthStore.getState().getAccessToken();
    const streamUrl = `/api/v2/edges/${edgeId}/diagnostics/${tool}/${requestId}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const eventSource = new EventSource(streamUrl);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('started', (e) => {
      const data = JSON.parse(e.data);
      setCurrentTest({
        requestId: data.requestId || requestId,
        type: tool,
        status: 'running',
        startTime: new Date(),
        results: [],
      });
    });

    eventSource.addEventListener('output', (e) => {
      const line = JSON.parse(e.data);
      setCurrentTest((prev) =>
        prev ? { ...prev, results: [...prev.results, line] } : null
      );
    });

    eventSource.addEventListener('completed', () => {
      setCurrentTest((prev) =>
        prev ? { ...prev, status: 'completed', endTime: new Date() } : null
      );
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.addEventListener('failed', (e) => {
      const errorMsg = JSON.parse(e.data);
      setCurrentTest((prev) =>
        prev
          ? {
              ...prev,
              status: 'failed',
              endTime: new Date(),
              error: errorMsg,
            }
          : null
      );
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.addEventListener('stopped', () => {
      setCurrentTest((prev) =>
        prev ? { ...prev, status: 'stopped', endTime: new Date() } : null
      );
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.onerror = () => {
      // Only show error if the stream was our active connection
      // EventSource fires onerror on connection close (normal for completed tests)
      if (eventSourceRef.current === eventSource) {
        console.error('SSE connection error');
        setCurrentTest((prev) => {
          // Only show error if test was still running (not already completed/failed/stopped)
          if (prev && prev.status === 'running') {
            setError('Connection to diagnostic stream lost');
          }
          return prev;
        });
        eventSource.close();
        eventSourceRef.current = null;
      }
    };
  };

  const runPing = async () => {
    if (!edgeId) {
      setError('Edge ID is required for diagnostics');
      return;
    }
    if (!pingForm.destination) {
      setError('Destination is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/api/v2/edges/${edgeId}/diagnostics/ping`, pingForm);
      const requestId = response.data.requestId;

      // Connect to SSE stream
      connectSSE(requestId, 'ping');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to start ping');
    } finally {
      setLoading(false);
    }
  };

  const stopPing = async () => {
    if (!edgeId || !currentTest?.requestId) return;

    try {
      await api.post(`/api/v2/edges/${edgeId}/diagnostics/ping/stop`, {
        requestId: currentTest.requestId,
      });
      setCurrentTest((prev) => (prev ? { ...prev, status: 'stopped', endTime: new Date() } : null));
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to stop ping');
    }
  };

  const runDiagnostic = async (type: string, endpoint: string, data: any) => {
    if (!edgeId) {
      setError('Edge ID is required for diagnostics');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/api/v2/edges/${edgeId}/diagnostics/${endpoint}`, data);
      const requestId = response.data.requestId;

      // Connect to SSE stream
      connectSSE(requestId, endpoint);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || `Failed to start ${type}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'stopped':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const renderInterfaceSelector = (value: string, onChange: (value: string) => void) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Source Interface
        {value && (
          <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 inline-flex items-center">
            <Network className="w-3 h-3 mr-1" />
            VPP Data Plane
          </span>
        )}
        {!value && (
          <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 inline-flex items-center">
            <Wifi className="w-3 h-3 mr-1" />
            Management Interface
          </span>
        )}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-base w-full"
      >
        <option value="">Management Interface (default)</option>
        {interfaces.map((iface) => {
          const hasIP = iface.ipv4 && iface.ipv4 !== '-';
          const ipDisplay = hasIP
            ? `${iface.ipv4}/${iface.subnet || '24'}`
            : 'No IP configured';
          return (
            <option key={iface.id} value={iface.name}>
              {iface.name} ({ipDisplay}) - {iface.operationalState}
            </option>
          );
        })}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        {value
          ? 'Tests through VPP data plane via selected interface'
          : 'Tests through Edge management interface'}
      </p>
    </div>
  );

  const tabs = [
    { id: 'ping', label: 'Ping', icon: Activity },
    { id: 'traceroute', label: 'Traceroute', icon: Globe },
    { id: 'dns', label: 'DNS Lookup', icon: Search },
    { id: 'portscan', label: 'Port Scan', icon: Search },
    { id: 'speedtest', label: 'Speed Test', icon: Gauge },
  ];

  if (!edgeId) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-amber-700 font-medium">
              Note: Edge ID is required to use diagnostic tools.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Network Diagnostics</h3>
        <p className="text-sm text-gray-600 mt-1">
          Run network diagnostic tests from the edge device. Select a source interface to test through VPP data plane.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px space-x-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const cap = capabilities.find((c) => c.id === tab.id);
            const isAvailable = cap?.available ?? true;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {!isAvailable && (
                  <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Soon</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Edge Offline Warning */}
      {!edgeOnline && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-amber-400 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">Edge Device Offline</p>
              <p className="text-sm text-amber-700 mt-1">
                The edge device is currently offline. Diagnostic tools are disabled until the device comes back online.
                All configured interfaces are shown below for reference.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ping Tab */}
      {activeTab === 'ping' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={pingForm.destination}
                onChange={(e) => setPingForm((prev) => ({ ...prev, destination: e.target.value }))}
                placeholder="IP address or hostname (e.g., 8.8.8.8)"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Count</label>
              <input
                type="number"
                value={pingForm.count}
                onChange={(e) => setPingForm((prev) => ({ ...prev, count: parseInt(e.target.value) || 5 }))}
                min={1}
                max={100}
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Packet Size</label>
              <input
                type="number"
                value={pingForm.packetSize}
                onChange={(e) => setPingForm((prev) => ({ ...prev, packetSize: parseInt(e.target.value) || 64 }))}
                min={32}
                max={65535}
                className="input-base w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInterfaceSelector(pingForm.sourceInterface, (value) =>
              setPingForm((prev) => ({ ...prev, sourceInterface: value }))
            )}
          </div>

          <div className="flex items-center space-x-3">
            {canUpdate && (
              <button
                onClick={runPing}
                disabled={!edgeOnline || loading || currentTest?.status === 'running'}
                className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                <span>Run Ping</span>
              </button>
            )}
            {canDelete && currentTest?.status === 'running' && currentTest.type === 'ping' && (
              <button onClick={stopPing} className="btn-secondary inline-flex items-center space-x-2">
                <Square className="w-4 h-4" />
                <span>Stop</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Traceroute Tab */}
      {activeTab === 'traceroute' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={tracerouteForm.destination}
                onChange={(e) => setTracerouteForm((prev) => ({ ...prev, destination: e.target.value }))}
                placeholder="IP address or hostname"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Max Hops</label>
              <input
                type="number"
                value={tracerouteForm.maxHops}
                onChange={(e) =>
                  setTracerouteForm((prev) => ({ ...prev, maxHops: parseInt(e.target.value) || 30 }))
                }
                min={1}
                max={64}
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Protocol</label>
              <select
                value={tracerouteForm.protocol}
                onChange={(e) => setTracerouteForm((prev) => ({ ...prev, protocol: e.target.value }))}
                className="input-base w-full"
              >
                <option value="icmp">ICMP</option>
                <option value="udp">UDP</option>
                <option value="tcp">TCP</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInterfaceSelector(tracerouteForm.sourceInterface, (value) =>
              setTracerouteForm((prev) => ({ ...prev, sourceInterface: value }))
            )}
          </div>

          {canUpdate && (
            <button
              onClick={() => runDiagnostic('traceroute', 'traceroute', tracerouteForm)}
              disabled={!edgeOnline || loading || !tracerouteForm.destination}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span>Run Traceroute</span>
            </button>
          )}
        </div>
      )}

      {/* DNS Tab */}
      {activeTab === 'dns' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hostname <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={dnsForm.hostname}
                onChange={(e) => setDnsForm((prev) => ({ ...prev, hostname: e.target.value }))}
                placeholder="e.g., google.com"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">DNS Server (optional)</label>
              <input
                type="text"
                value={dnsForm.dnsServer}
                onChange={(e) => setDnsForm((prev) => ({ ...prev, dnsServer: e.target.value }))}
                placeholder="e.g., 8.8.8.8"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Record Type</label>
              <select
                value={dnsForm.recordType}
                onChange={(e) => setDnsForm((prev) => ({ ...prev, recordType: e.target.value }))}
                className="input-base w-full"
              >
                <option value="A">A (IPv4)</option>
                <option value="AAAA">AAAA (IPv6)</option>
                <option value="MX">MX (Mail)</option>
                <option value="TXT">TXT</option>
                <option value="CNAME">CNAME</option>
                <option value="NS">NS</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInterfaceSelector(dnsForm.sourceInterface, (value) =>
              setDnsForm((prev) => ({ ...prev, sourceInterface: value }))
            )}
          </div>

          {canUpdate && (
            <button
              onClick={() => runDiagnostic('dns', 'dns', dnsForm)}
              disabled={!edgeOnline || loading || !dnsForm.hostname}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span>Lookup</span>
            </button>
          )}
        </div>
      )}

      {/* Port Scan Tab */}
      {activeTab === 'portscan' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={portScanForm.target}
                onChange={(e) => setPortScanForm((prev) => ({ ...prev, target: e.target.value }))}
                placeholder="IP address or hostname"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ports <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={portScanForm.ports}
                onChange={(e) => setPortScanForm((prev) => ({ ...prev, ports: e.target.value }))}
                placeholder="80,443 or 1-1024"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timeout (sec)</label>
              <input
                type="number"
                value={portScanForm.timeout}
                onChange={(e) =>
                  setPortScanForm((prev) => ({ ...prev, timeout: parseInt(e.target.value) || 5 }))
                }
                min={1}
                max={30}
                className="input-base w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInterfaceSelector(portScanForm.sourceInterface, (value) =>
              setPortScanForm((prev) => ({ ...prev, sourceInterface: value }))
            )}
          </div>

          {canUpdate && (
            <button
              onClick={() => runDiagnostic('portscan', 'portscan', portScanForm)}
              disabled={!edgeOnline || loading || !portScanForm.target}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span>Scan Ports</span>
            </button>
          )}
        </div>
      )}

      {/* Speed Test Tab */}
      {activeTab === 'speedtest' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Server (optional)</label>
              <input
                type="text"
                value={speedTestForm.server}
                onChange={(e) => setSpeedTestForm((prev) => ({ ...prev, server: e.target.value }))}
                placeholder="Auto-select best server"
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (sec)</label>
              <input
                type="number"
                value={speedTestForm.duration}
                onChange={(e) =>
                  setSpeedTestForm((prev) => ({ ...prev, duration: parseInt(e.target.value) || 10 }))
                }
                min={5}
                max={60}
                className="input-base w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
              <select
                value={speedTestForm.direction}
                onChange={(e) => setSpeedTestForm((prev) => ({ ...prev, direction: e.target.value }))}
                className="input-base w-full"
              >
                <option value="both">Both</option>
                <option value="download">Download Only</option>
                <option value="upload">Upload Only</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInterfaceSelector(speedTestForm.sourceInterface, (value) =>
              setSpeedTestForm((prev) => ({ ...prev, sourceInterface: value }))
            )}
          </div>

          {canUpdate && (
            <button
              onClick={() => runDiagnostic('speedtest', 'speedtest', speedTestForm)}
              disabled={!edgeOnline || loading}
              className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              <span>Start Speed Test</span>
            </button>
          )}
        </div>
      )}

      {/* Results Panel */}
      {currentTest && (
        <div className="bg-gray-900 rounded-lg p-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {getStatusIcon(currentTest.status)}
              <span className="text-sm font-medium text-white capitalize">
                {currentTest.type} - {currentTest.status}
              </span>
            </div>
            <span className="text-xs text-gray-400">
              Request ID: {currentTest.requestId}
            </span>
          </div>

          {currentTest.error ? (
            <div className="text-red-400 text-sm font-mono">{currentTest.error}</div>
          ) : (
            <div className="font-mono text-sm text-green-400 max-h-96 overflow-y-auto">
              {currentTest.results.map((line, index) => (
                <div key={index}>{line || '\u00A0'}</div>
              ))}
              {currentTest.status === 'running' && currentTest.results.length === 0 && (
                <div className="animate-pulse">Waiting for results...</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiagnosticTools;
